import { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  Pencil, Type, Square, Circle, ArrowRight, MessageSquare, 
  Highlighter, Move,
  ChevronLeft, ChevronRight, FileText, Loader2, Home
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import '@/styles/site-office.css';
// DP274-P3a-3 — the page box is the reference frame; the two converters live in
// their own module so they can be tested on their own, and are re-exported here.
import { toSheet, toContent } from '@/utils/sheetSpace';
import { hitPart, applyDrag, handlesOf, cursorFor, boxEdge } from '@/utils/markEdit';
import { codeFor, kindOf } from '@/utils/markCodes';
import '@/styles/markup-tools.css';

// DP274-P3a-2 — the worker is ours, served from this origin. scripts/sync-pdf-worker.js
// copies it out of node_modules on prestart/prebuild and fails loudly if it is
// missing; utils/pdfWorker sets the src and asserts the version matches.
import '@/utils/pdfWorker'; // side-effect: workerSrc = /pdf.worker.min.mjs


// DP274-P3a-1 — the redline tools, in the order the mockup shows them. Note,
// Text and Rectangle are gone from the bar; marks of those types still draw,
// because old sheets carry them.
export { toSheet, toContent };

const MARKUP_TOOLS = [
  { id: 'point', label: 'Point', cursor: 'crosshair' },
  { id: 'arrow', label: 'Arrow', cursor: 'crosshair' },
  { id: 'bracket', label: 'Bracket', cursor: 'crosshair' },
  { id: 'cloud', label: 'Cloud', cursor: 'crosshair' },
  { id: 'highlight', label: 'Highlight', cursor: 'crosshair' },
  { id: 'pen', label: 'Pen', cursor: 'crosshair' },
  { id: 'select', label: 'Select', cursor: 'default' },
];

// A mark's colour says WHO made it, so nobody chooses it: the firm draws in
// the brand, a party in the accent. Read from the token file at mount.
const readTokens = () => {
  const css = getComputedStyle(document.documentElement);
  const pick = (name, fallback) => (css.getPropertyValue(name) || '').trim() || fallback;
  return {
    firm: pick('--km-brand', '#70170F'),
    party: pick('--km-accent', '#BC5D41'),
    paper: pick('--km-paper', '#FAF7F2'),
    faint: pick('--km-ink-3', '#9B8F7F'),
    // MK-5a — no raw fallbacks here: this file is frozen against new colour
    // literals, and all four of these variables live in keymade-tokens.css.
    ink: pick('--km-ink', ''),
    blue: pick('--km-info', ''),
    green: pick('--km-ok', ''),
    yellow: pick('--km-key', ''),
  };
};

// MK-5a — the palette and the weights. Yellow is the highlighter's colour only.
const PALETTE = (tk) => [
  ['red', tk.firm], ['blue', tk.blue], ['green', tk.green], ['ink', tk.ink], ['yellow', tk.yellow],
];
const WEIGHTS = [1, 1.5, 2.5];

// MK-5b — the tool icons: one stroke each, drawn in the rail's ink.
const ICONS = {
  point: <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5" /><path d="M8 5.5v5" /></svg>,
  arrow: <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M13 3 4 12M4 7v5h5" /></svg>,
  bracket: <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 5c0-1 .5-1.5 1.5-1.5H6M2 11c0 1 .5 1.5 1.5 1.5H6M14 5c0-1-.5-1.5-1.5-1.5H10M14 11c0 1-.5 1.5-1.5 1.5H10M8 3v10" /></svg>,
  cloud: <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 12a2.5 2.5 0 0 1-.6-4.9A3.5 3.5 0 0 1 10.6 6 2.5 2.5 0 0 1 12 11.9z" /></svg>,
  highlight: <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 10.5 9.5 4l2.5 2.5L5.5 13H3zM2 14h12" /></svg>,
  pen: <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 13c3-6 5-6 6-2s3 4 6-3" /></svg>,
  select: <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 2l10 6-4 1-2 4z" /></svg>,
};
const COLOURABLE = ['point', 'pen', 'highlight', 'cloud', 'arrow', 'bracket'];
const WEIGHTED = ['arrow', 'bracket', 'cloud', 'pen', 'highlight'];

const maxNumber = (list) => (list || []).reduce((n, m) => Math.max(n, m.number || 0), 0);

// MK-1 — the zoom that shows a whole sheet inside its box, with a margin.
export const fitZoom = (dims, box, margin = 24) => {
  if (!dims?.width || !dims?.height || !box) return 1;
  const fit = Math.min((box.clientWidth - margin * 2) / dims.width, (box.clientHeight - margin * 2) / dims.height);
  return Math.max(0.05, Math.min(fit, 4));
};

const MONO = "'IBM Plex Mono', ui-monospace, monospace";

export default function DrawingMarkupEditor({ 
  imageUrl, 
  existingMarkups = [], 
  onSave, 
  onClose,
  readOnly = false,
  drawingTitle = 'Drawing',
  drawingNumber = '',
  projectName = '',
  versionNumber = 1,
  authorKind = 'firm',
  onSaveMerged,
  apiBase,
  // DP274-P3a-2 — the same editor serves the firm and a party. The two calls
  // the schedule makes are injectable, because a party's doors are their own
  // token routes; left out, they are the firm's routes as P3a-1 wrote them.
  onReply,
  onStatus,
  // MK-2b — deleting a saved mark goes through its own door; left out, the firm's drawing door (apiBase) is used.
  onDeleteMark,
  saveButtons,
  embedded = false,
  // A shared sheet is served through a token route with no extension, so the
  // caller says what it is rather than the URL guessing.
  fileKind,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const imageRef = useRef(null);
  const [tool, setTool] = useState('point');
  const [tokens, setTokens] = useState(readTokens);
  const [inkChoice, setInkChoice] = useState(null);      // null = the author's own colour
  const [weightChoice, setWeightChoice] = useState(1.5);
  // MK-5b — the sheet dimmer: 100 = as drawn, 25 = faint and grey. Remembered per browser.
  const [dim, setDim] = useState(() => {
    try { const v = parseInt(localStorage.getItem('km.markup.dim') || '100', 10); return Number.isFinite(v) ? Math.min(100, Math.max(25, v)) : 100; } catch { return 100; }
  });
  const setDimKept = (v) => { setDim(v); try { localStorage.setItem('km.markup.dim', String(v)); } catch { /* fine */ } };
  const dimFilter = dim >= 100 ? 'none' : `opacity(${dim / 100}) grayscale(${(100 - dim) / 100})`;
  const color = inkChoice || (authorKind === 'party' ? tokens.party : tokens.firm);
  // DP274-P3a-1 — only what has been touched is posted, so a save never rubs
  // out a mark the other side added while this sheet was open.
  const dirtyRef = useRef(new Set());
  const [replyText, setReplyText] = useState({});
  // DP274-P3a-2 — a round goes back with a line to the firm, asked for here.
  const [asking, setAsking] = useState(null);
  const [askText, setAskText] = useState('');
  const [markups, setMarkups] = useState(existingMarkups);
  const [selectedMarkup, setSelectedMarkup] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentPoints, setCurrentPoints] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [textPosition, setTextPosition] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 800, height: 600 });
  const [history, setHistory] = useState([]);
  const [loadedImage, setLoadedImage] = useState(null);
  
  // PDF state
  const [isPdf, setIsPdf] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  // The page box, in content coordinates. Until it is known nothing can be
  // placed, because there is nothing to measure a fraction against.
  const [pageBox, setPageBox] = useState(null);
  const [legacyCount, setLegacyCount] = useState(0);
  const pageElRef = useRef(null);
  // MK-1 — fitted once per sheet; Space held pans like the middle button.
  const fittedRef = useRef(false);
  const spaceRef = useRef(false);
  // MK-2 — the drag in progress on a selected mark: which part, and where the
  // pointer last was (content coords).
  const dragRef = useRef(null);
  const [hoverPart, setHoverPart] = useState(null);
  // MK-2/C2 — true once the reader has moved or zoomed the view themselves.
  const touchedRef = useRef(false);
  // MK-3 — the note box of each pointed arrow, measured as it is drawn (content units), so a press can find it.
  const boxSizeRef = useRef({});
  const withBox = (m) => (m.type === 'arrow' && m.head === 'start' ? { ...m, box: boxSizeRef.current[m.id] || { w: 60 / zoom, h: 24 / zoom } } : m);

  // Detect if URL is PDF
  useEffect(() => {
    if (imageUrl) {
      fittedRef.current = false;
      touchedRef.current = false;
      const isPdfFile = fileKind
        ? fileKind === 'pdf'
        : imageUrl.split('?')[0].toLowerCase().endsWith('.pdf') ||
          imageUrl.includes('/pdf') ||
          imageUrl.includes('application/pdf');
      setIsPdf(isPdfFile);
      if (!isPdfFile) {
        // Load as image
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          setImageDimensions({ width: img.width, height: img.height });
          // The image is drawn at 0,0 at its natural size, so that IS the box.
          setPageBox({ left: 0, top: 0, width: img.width, height: img.height });
          setLoadedImage(img);
          setImageLoaded(true);
        };
        img.onerror = () => {
          console.error('Failed to load image:', imageUrl);
          // Still mark as loaded but with no image
          setImageLoaded(true);
          setLoadedImage(null);
        };
        // Handle relative URLs
        const fullUrl = imageUrl.startsWith('http') ? imageUrl : 
                       imageUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}` : imageUrl;
        img.src = fullUrl;
      } else {
        setImageLoaded(true);
      }
    } else {
      // No URL, show placeholder
      setImageLoaded(true);
    }
  }, [imageUrl, fileKind]);

  // PDF handlers
  const onDocumentLoadSuccess = ({ numPages: pages }) => {
    setNumPages(pages);
    setPdfLoading(false);
    setImageLoaded(true);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setPdfError('Failed to load PDF');
    setPdfLoading(false);
    setImageLoaded(true);
  };

  const onPageLoadSuccess = (page) => {
    setImageDimensions({ width: page.width, height: page.height });
  };

  // The page's box in CONTENT coordinates: the page sits in the middle of the
  // container at whatever the zoom is, and the overlay canvas reads the same
  // numbers, so a mark and the sheet cannot drift apart.
  const measurePage = useCallback(() => {
    const canvas = canvasRef.current;
    const sheet = pdfContainerRef.current?.querySelector('.react-pdf__Page canvas');
    if (!canvas || !sheet) return;
    const overlay = canvas.getBoundingClientRect();
    const page = sheet.getBoundingClientRect();
    if (!page.width || !page.height) return;
    // Read the page off the screen and undo the view, so the box is where the
    // sheet sits before zoom and pan - the same frame a mark is drawn in.
    setPageBox({
      left: (page.left - overlay.left - pan.x) / zoom,
      top: (page.top - overlay.top - pan.y) / zoom,
      width: page.width / zoom,
      height: page.height / zoom,
    });
  }, [zoom, pan.x, pan.y]);

  useEffect(() => {
    if (!isPdf) return undefined;
    const settle = setTimeout(measurePage, 0);
    const box = containerRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => measurePage());
    observer.observe(box);
    return () => { clearTimeout(settle); observer.disconnect(); };
  }, [isPdf, measurePage, currentPage, numPages, imageDimensions]);

  // Where the page is parked so it stays centred as the zoom changes; the box a
  // mark lives in is then measured off the screen, never assumed from this.
  const centredPage = {
    left: Math.max(0, ((containerRef.current?.clientWidth || 0) / zoom - imageDimensions.width) / 2),
    top: Math.max(0, ((containerRef.current?.clientHeight || 0) / zoom - imageDimensions.height) / 2),
  };

  // DP274-P3a-3 — the carry-over. A pixel mark never recorded the screen it was
  // made on, so no server can recover its true spot: the screen that opens it
  // next is the best frame available. Convert once, mark it dirty, and the next
  // save (or Send round / Save for later) writes the fractions down for good.
  useEffect(() => {
    if (!pageBox) return;
    const stale = markups.filter((m) => (m.space || 'px') !== 'sheet');
    if (stale.length === 0) return;
    stale.forEach((m) => dirtyRef.current.add(m.id));
    setLegacyCount(stale.length);
    setMarkups((prev) => prev.map((m) => ((m.space || 'px') === 'sheet' ? m : toSheet(m, pageBox))));
  }, [pageBox, markups]);

  // Draw canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageLoaded) return;

    const ctx = canvas.getContext('2d');
    const container = containerRef.current;
    
    // MK-5a — draw at device pixels so a hairline is a hairline on a HiDPI screen.
    const dpr = window.devicePixelRatio || 1;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    if (canvas.width !== Math.round(cw * dpr) || canvas.height !== Math.round(ch * dpr)) {
      canvas.width = Math.round(cw * dpr);
      canvas.height = Math.round(ch * dpr);
    }
    canvas.style.width = `${cw}px`;
    canvas.style.height = `${ch}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Clear canvas. DP274-P3a-2 — over a PDF the overlay must be GLASS: an
    // opaque fill here painted the sheet out and left the redlines floating on
    // black, which is what both editors were showing.
    ctx.clearRect(0, 0, cw, ch);
    if (!isPdf) {
      ctx.fillStyle = '#1A1A1A';
      ctx.fillRect(0, 0, cw, ch);
    }

    // Apply zoom and pan
    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    // Draw the actual image if loaded
    if (loadedImage && !isPdf) {
      try {
        // MK-5b — only the sheet is dimmed; the marks are drawn after the restore.
        ctx.save();
        ctx.filter = dimFilter;
        ctx.drawImage(loadedImage, 0, 0, imageDimensions.width, imageDimensions.height);
        ctx.restore();
      } catch (e) {
        console.error('Error drawing image:', e);
        // Fallback to placeholder
        ctx.fillStyle = '#2A2A2A';
        ctx.fillRect(0, 0, imageDimensions.width || 800, imageDimensions.height || 600);
      }
    } else if (!isPdf) {
      // No image loaded - draw placeholder with grid
      ctx.fillStyle = '#2A2A2A';
      ctx.fillRect(0, 0, imageDimensions.width || 800, imageDimensions.height || 600);
      
      // Draw grid pattern
      ctx.strokeStyle = '#3A3A3A';
      ctx.lineWidth = 0.5;
      for (let x = 0; x < (imageDimensions.width || 800); x += 50) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, imageDimensions.height || 600);
        ctx.stroke();
      }
      for (let y = 0; y < (imageDimensions.height || 600); y += 50) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(imageDimensions.width || 800, y);
        ctx.stroke();
      }
      
      // Draw "No Image" text
      ctx.fillStyle = '#6B6B6B';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No image available', (imageDimensions.width || 800) / 2, (imageDimensions.height || 600) / 2);
    }

    // DP274-P3a-1 — the line weight rule: a mark is a hairline ON SCREEN at
    // any zoom, because a redline is a hairline on paper.
    const px = (n) => n / zoom;
    markups.forEach((mark, index) => {
      if ((mark.page || 1) !== currentPage) return;   // page-aware
      const markup = toContent(mark, pageBox);
      drawMarkup(ctx, markup, selectedMarkup === index, px, tokens, codeFor(mark, markups), !mark.number);
    });

    // Draw current drawing operation
    if (isDrawing && startPoint) {
      ctx.strokeStyle = color;
      ctx.lineWidth = px(1.5);
      ctx.setLineDash([px(4), px(4)]);
      
      if (tool === 'highlight') {
        const width = (currentPoints[0]?.x || startPoint.x) - startPoint.x;
        const height = (currentPoints[0]?.y || startPoint.y) - startPoint.y;
        if (tool === 'highlight') {
          ctx.fillStyle = color + '40';
          ctx.fillRect(startPoint.x, startPoint.y, width, height);
        }
        ctx.strokeRect(startPoint.x, startPoint.y, width, height);
      } else if (tool === 'bracket') {
        const endPoint = currentPoints[currentPoints.length - 1] || startPoint;
        ctx.strokeStyle = color; ctx.lineWidth = px(1.5);
        ctx.beginPath(); ctx.moveTo(startPoint.x, startPoint.y); ctx.lineTo(endPoint.x, endPoint.y); ctx.stroke();
      } else if (tool === 'arrow') {
        const endPoint = currentPoints[0] || startPoint;
        drawArrow(ctx, endPoint.x, endPoint.y, startPoint.x, startPoint.y, color, px);   // MK-3: the head sits where the press was
      } else if (tool === 'pen' && currentPoints.length > 0) {
        ctx.beginPath();
        ctx.moveTo(startPoint.x, startPoint.y);
        currentPoints.forEach(point => {
          ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();
      } else if (tool === 'cloud') {
        const endPoint = currentPoints[0] || startPoint;
        drawCloud(ctx, startPoint.x, startPoint.y, endPoint.x, endPoint.y, color, px);
      }
      
      ctx.setLineDash([]);
    }

    ctx.restore();
  }, [imageLoaded, imageDimensions, markups, selectedMarkup, isDrawing, startPoint, currentPoints, tool, color, zoom, pan, loadedImage, isPdf, currentPage, tokens, pageBox, dimFilter]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Draw individual markup. DP274-P3a-1 — every stroke is px(1.5); a Point is
  // an open numbered circle; a mark's own line of text is drawn beside it.
  const drawMarkup = (ctx, markup, isSelected, px, tk, code, provisional) => {
    const ink = markup.color || tk.firm;
    ctx.strokeStyle = ink;
    ctx.fillStyle = ink;
    ctx.lineWidth = isSelected ? px(2.25) : px(1.5);
    ctx.setLineDash([]);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    const label = (text, x, y) => {
      if (!String(text || '').trim()) return;
      ctx.font = `${px(11)}px ${MONO}`;
      ctx.fillStyle = ink;
      ctx.fillText(text, x, y);
    };

    // MK-4 — the code on a paper tag at a corner of the mark.
    const tag = (x, y) => {
      ctx.font = `600 ${px(10)}px ${MONO}`;
      const tw = ctx.measureText(code).width + px(8);
      const th = px(14);
      ctx.fillStyle = tk.paper;
      ctx.fillRect(x, y - th, tw, th);
      ctx.strokeStyle = ink;
      ctx.lineWidth = px(1);
      ctx.strokeRect(x, y - th, tw, th);
      ctx.fillStyle = provisional ? tk.faint : ink;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(code, x + px(4), y - px(3.5));
    };

    // MK-4 — every stroke twice: a paper casing underneath, then the ink.
    const w = px(markup.weight || 1.5) * (isSelected ? 1.5 : 1);
    const cased = (path) => {
      ctx.save();
      ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      ctx.strokeStyle = tk.paper; ctx.lineWidth = w + px(3); path(); ctx.stroke();
      ctx.strokeStyle = ink; ctx.lineWidth = w; path(); ctx.stroke();
      ctx.restore();
    };

    switch (markup.type) {
      case 'point': {
        const r = px(10);
        ctx.beginPath();
        ctx.arc(markup.x, markup.y, r, 0, Math.PI * 2);
        ctx.fillStyle = tk.paper;
        ctx.fill();
        ctx.lineWidth = w;
        ctx.strokeStyle = ink;
        ctx.stroke();
        if (code) {
          ctx.font = `600 ${px(11)}px ${MONO}`;
          ctx.fillStyle = provisional ? tk.faint : ink;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(code), markup.x, markup.y);
          ctx.textAlign = 'left';
          ctx.textBaseline = 'alphabetic';
          // RS-1 — a contractor's note carries its RFI number beside the pin.
          if (markup.rfi_number) label(markup.rfi_number, markup.x + r + px(4), markup.y + px(4));
        }
        break;
      }

      case 'rectangle':
        cased(() => { ctx.beginPath(); ctx.rect(markup.x, markup.y, markup.width, markup.height); });
        tag(Math.min(markup.x, markup.x + (markup.width || 0)), Math.min(markup.y, markup.y + (markup.height || 0)) - px(2));
        break;

      case 'highlight':
        ctx.fillStyle = ink + '33';
        ctx.fillRect(markup.x, markup.y, markup.width, markup.height);
        cased(() => { ctx.beginPath(); ctx.rect(markup.x, markup.y, markup.width, markup.height); });
        tag(Math.min(markup.x, markup.x + (markup.width || 0)), Math.min(markup.y, markup.y + (markup.height || 0)) - px(2));
        break;

      case 'arrow': {
        if (markup.head === 'start') {
          // MK-3 — x,y is the point; the note box is centred at the far end.
          const cx = markup.x + (markup.width || 100);
          const cy = markup.y + (markup.height || 0);
          ctx.font = `${px(12)}px ${MONO}`;
          const lines = String(markup.text || '').split('\n').filter((l, i, a) => l.trim() || a.length === 1);
          const shown = lines.length && lines[0] !== '' ? lines : ['…'];
          const bw = Math.max(px(60), ...shown.map((l) => ctx.measureText(l).width)) + px(16);
          const bh = shown.length * px(16) + px(12);
          boxSizeRef.current[markup.id] = { w: bw, h: bh };
          const e = boxEdge(cx, cy, bw, bh, markup.x, markup.y);
          cased(() => { ctx.beginPath(); ctx.moveTo(e.x, e.y); ctx.lineTo(markup.x, markup.y); });
          cased(() => arrowHeadPath(ctx, e.x, e.y, markup.x, markup.y, px));
          ctx.fillStyle = tk.paper;
          ctx.fillRect(cx - bw / 2, cy - bh / 2, bw, bh);
          ctx.strokeStyle = ink;
          ctx.lineWidth = px(1);
          ctx.strokeRect(cx - bw / 2, cy - bh / 2, bw, bh);
          ctx.fillStyle = markup.text ? ink : tk.faint;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'alphabetic';
          shown.forEach((l, i) => ctx.fillText(l, cx - bw / 2 + px(8), cy - bh / 2 + px(18) + i * px(16)));
          tag(cx - bw / 2, cy - bh / 2 - px(2));
          break;
        }
        const endX = markup.x + (markup.width || 100);
        const endY = markup.y + (markup.height || 0);
        cased(() => { ctx.beginPath(); ctx.moveTo(markup.x, markup.y); ctx.lineTo(endX, endY); });
        cased(() => arrowHeadPath(ctx, markup.x, markup.y, endX, endY, px));
        tag(markup.x, markup.y - px(2));   // at the tail (legacy arrows)
        break;
      }

      case 'bracket': {
        // MK-6/MK-5a — a curly brace spanning A to B; the code sits at the cusp.
        const bx2 = markup.x + (markup.width || 100);
        const by2 = markup.y + (markup.height || 0);
        const depth = px(12);
        cased(() => bracePath(ctx, markup.x, markup.y, bx2, by2, depth, px));
        const L = Math.hypot(bx2 - markup.x, by2 - markup.y) || 1;
        const nx = -(by2 - markup.y) / L; const ny = (bx2 - markup.x) / L;
        const cx = (markup.x + bx2) / 2 + nx * (depth + px(4));
        const cy = (markup.y + by2) / 2 + ny * (depth + px(4));
        ctx.font = `600 ${px(10)}px ${MONO}`;
        const tw = ctx.measureText(code).width + px(8);
        tag(cx - tw / 2, cy + px(7));
        break;
      }

      case 'cloud':
        cased(() => cloudPath(ctx, markup.x, markup.y, markup.x + (markup.width || 100), markup.y + (markup.height || 80)));
        tag(Math.min(markup.x, markup.x + (markup.width || 0)), Math.min(markup.y, markup.y + (markup.height || 0)) - px(2));
        break;

      case 'note':
      case 'text':
        // No highlighter box: the words are the mark.
        label(markup.text, markup.x, markup.y);
        break;

      case 'pen':
      case 'freehand':
        if (markup.points && markup.points.length > 0) {
          cased(() => { ctx.beginPath(); ctx.moveTo(markup.x, markup.y); markup.points.forEach((p) => ctx.lineTo(p.x, p.y)); });
          tag(Math.min(markup.x, ...markup.points.map((p) => p.x)), Math.min(markup.y, ...markup.points.map((p) => p.y)) - px(2));
        }
        break;

      default:
        break;
    }

    if (isSelected) {
      const h = px(7);
      ctx.fillStyle = tk.paper;
      ctx.strokeStyle = ink;
      ctx.lineWidth = px(1);
      handlesOf(markup).filter((hd) => !(markup.head === 'start' && hd.part === 'end')).forEach(({ x: hx, y: hy }) => {
        ctx.fillRect(hx - h / 2, hy - h / 2, h, h);
        ctx.strokeRect(hx - h / 2, hy - h / 2, h, h);
      });
      if (markup.type === 'pen' || markup.type === 'freehand') {
        // a dotted box says "this moves as one"
        const xs = [markup.x, ...(markup.points || []).map((p) => p.x)];
        const ys = [markup.y, ...(markup.points || []).map((p) => p.y)];
        const x1 = Math.min(...xs) - px(4); const y1 = Math.min(...ys) - px(4);
        ctx.setLineDash([px(4), px(3)]);
        ctx.strokeRect(x1, y1, Math.max(...xs) - x1 + px(4), Math.max(...ys) - y1 + px(4));
        ctx.setLineDash([]);
      }
    }
  };

  // MK-5a — a curly brace from A to B: the body runs at half depth, the cusp at full depth, at the middle.
  const bracePath = (ctx, x1, y1, x2, y2, depth, px) => {
    const L = Math.hypot(x2 - x1, y2 - y1) || 1;
    const ux = (x2 - x1) / L; const uy = (y2 - y1) / L;     // along
    const nx = -uy; const ny = ux;                            // across (the cusp side)
    const r = Math.min(px(8), L / 4);
    const P = (s, o) => [x1 + ux * s + nx * o, y1 + uy * s + ny * o];
    const h = depth / 2;
    ctx.beginPath();
    ctx.moveTo(...P(0, 0));
    ctx.quadraticCurveTo(...P(0, h), ...P(r, h));
    ctx.lineTo(...P(L / 2 - r, h));
    ctx.quadraticCurveTo(...P(L / 2, h), ...P(L / 2, depth));
    ctx.quadraticCurveTo(...P(L / 2, h), ...P(L / 2 + r, h));
    ctx.lineTo(...P(L - r, h));
    ctx.quadraticCurveTo(...P(L, h), ...P(L, 0));
  };

  // MK-4 — the head as a PATH, so the casing and the ink can share it.
  const arrowHeadPath = (ctx, fromX, fromY, toX, toY, px) => {
    const headLength = px(9);
    const angle = Math.atan2(toY - fromY, toX - fromX);
    ctx.beginPath();
    ctx.moveTo(toX - headLength * Math.cos(angle - Math.PI / 6), toY - headLength * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(toX, toY);
    ctx.lineTo(toX - headLength * Math.cos(angle + Math.PI / 6), toY - headLength * Math.sin(angle + Math.PI / 6));
  };

  // Draw arrow helper (the in-progress preview still strokes straight away)
  const drawArrow = (ctx, fromX, fromY, toX, toY, arrowColor, px) => {
    ctx.strokeStyle = arrowColor; ctx.lineWidth = px(1.5);
    ctx.beginPath(); ctx.moveTo(fromX, fromY); ctx.lineTo(toX, toY); ctx.stroke();
    arrowHeadPath(ctx, fromX, fromY, toX, toY, px); ctx.stroke();
  };

  // MK-4 — the cloud as a PATH
  const cloudPath = (ctx, x1, y1, x2, y2) => {
    const width = Math.abs(x2 - x1);
    const height = Math.abs(y2 - y1);
    const minX = Math.min(x1, x2);
    const minY = Math.min(y1, y2);
    
    const bumps = Math.max(4, Math.floor(width / 30));
    const bumpRadius = width / bumps / 2;
    
    ctx.beginPath();
    
    // Top bumps
    for (let i = 0; i < bumps; i++) {
      const centerX = minX + bumpRadius + (i * bumpRadius * 2);
      ctx.arc(centerX, minY, bumpRadius, Math.PI, 0);
    }
    
    // Right bumps
    const rightBumps = Math.max(2, Math.floor(height / 30));
    const rightBumpRadius = height / rightBumps / 2;
    for (let i = 0; i < rightBumps; i++) {
      const centerY = minY + rightBumpRadius + (i * rightBumpRadius * 2);
      ctx.arc(minX + width, centerY, rightBumpRadius, -Math.PI / 2, Math.PI / 2);
    }
    
    // Bottom bumps
    for (let i = bumps - 1; i >= 0; i--) {
      const centerX = minX + bumpRadius + (i * bumpRadius * 2);
      ctx.arc(centerX, minY + height, bumpRadius, 0, Math.PI);
    }
    
    // Left bumps
    for (let i = rightBumps - 1; i >= 0; i--) {
      const centerY = minY + rightBumpRadius + (i * rightBumpRadius * 2);
      ctx.arc(minX, centerY, rightBumpRadius, Math.PI / 2, -Math.PI / 2);
    }
    
    ctx.closePath();
  };

  // Draw cloud helper (the in-progress preview still strokes straight away)
  const drawCloud = (ctx, x1, y1, x2, y2, cloudColor, px) => {
    ctx.strokeStyle = cloudColor; ctx.lineWidth = px(1.5);
    cloudPath(ctx, x1, y1, x2, y2); ctx.stroke();
  };

  // Get canvas coordinates from mouse event
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom
    };
  };

  // DP274-P3a-1 — what the finger or the cursor actually landed on. Distances
  // are in screen pixels turned back into sheet units, so a fat finger and a
  // deep zoom are the same touch.
  const hitIndex = (coords) => {
    const near = 12 / zoom;
    const thin = 6 / zoom;
    for (let i = markups.length - 1; i >= 0; i -= 1) {
      const m = toContent(markups[i], pageBox);
      if ((m.page || 1) !== currentPage) continue;
      if (m.type === 'arrow' && m.head === 'start' && hitPart(withBox(m), coords, near, thin)) return i;
      if (m.type === 'point') {
        if (Math.hypot(coords.x - m.x, coords.y - m.y) <= near) return i;
      } else if (m.type === 'arrow' || m.type === 'bracket') {
        const x2 = m.x + (m.width || 100);
        const y2 = m.y + (m.height || 0);
        const len = Math.hypot(x2 - m.x, y2 - m.y) || 1;
        const d = Math.abs((x2 - m.x) * (m.y - coords.y) - (m.x - coords.x) * (y2 - m.y)) / len;
        if (d <= thin) return i;
      } else if (m.type === 'pen' || m.type === 'freehand') {
        if ((m.points || []).some(pt => Math.hypot(coords.x - pt.x, coords.y - pt.y) <= thin)) return i;
      } else {
        const x1 = Math.min(m.x, m.x + (m.width || 50));
        const y1 = Math.min(m.y, m.y + (m.height || 20));
        const x2 = Math.max(m.x, m.x + (m.width || 50));
        const y2 = Math.max(m.y, m.y + (m.height || 20));
        if (coords.x >= x1 && coords.x <= x2 && coords.y >= y1 && coords.y <= y2) return i;
      }
    }
    return null;
  };

  const activePointer = useRef(null);

  // Pointer handlers — a finger, a stylus and a mouse are the same thing here.
  const handlePointerDown = (e) => {
    if (activePointer.current !== null) return;   // one pointer at a time
    activePointer.current = e.pointerId;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not fatal */ }

    // MK-1 — the middle button (or Alt+drag, or Space held) pans whatever tool
    // is up. MK-2/C1 — a pan needs no sheet and no write right, so it sits
    // above both guards.
    if (e.button === 1 || (e.button === 0 && (e.altKey || spaceRef.current))) {
      touchedRef.current = true;
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      return;
    }

    if (readOnly || !pageBox) { activePointer.current = null; }   // the press is let go of again
    if (readOnly) return;
    if (!pageBox) return;   // no sheet measured yet: there is nothing to place a mark on

    const coords = getCanvasCoords(e);

    if (tool === 'select') {
      const grab = 8 / zoom;
      const thin = 6 / zoom;
      // The selected mark's handles win first, then whatever is under the pointer.
      let index = null;
      let part = null;
      if (selectedMarkup !== null && markups[selectedMarkup]) {
        const p = hitPart(withBox(toContent(markups[selectedMarkup], pageBox)), coords, grab, thin);
        if (p) { index = selectedMarkup; part = p; }
      }
      if (index === null) {
        index = hitIndex(coords);
        part = index !== null ? (hitPart(withBox(toContent(markups[index], pageBox)), coords, grab, thin) || 'move') : null;
      }
      setSelectedMarkup(index);
      if (index !== null) {
        saveHistory();
        dragRef.current = { index, part, last: coords, moved: false };
      }
    } else if (tool === 'point') {
      // A tap IS the mark; the text line opens straight after it.
      saveHistory();
      addMark({ type: 'point', x: coords.x, y: coords.y }, true);
      activePointer.current = null;
    } else {
      setIsDrawing(true);
      setStartPoint(coords);
      setCurrentPoints([]);
      saveHistory();
    }
  };

  const handlePointerMove = (e) => {
    if (activePointer.current !== null && e.pointerId !== activePointer.current) return;
    if (isPanning && lastPanPoint) {
      setPan({
        x: pan.x + (e.clientX - lastPanPoint.x),
        y: pan.y + (e.clientY - lastPanPoint.y)
      });
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      return;
    }

    if (dragRef.current) {
      const coords = getCanvasCoords(e);
      const d = dragRef.current;
      const dx = coords.x - d.last.x;
      const dy = coords.y - d.last.y;
      if (dx || dy) {
        d.last = coords;
        d.moved = true;
        setMarkups((prev) => prev.map((m, i) => (i === d.index
          ? toSheet(applyDrag(toContent(m, pageBox), d.part, dx, dy), pageBox)
          : m)));
      }
      return;
    }

    if (tool === 'select' && !readOnly) {
      // the cursor says what a press would do
      const coords = getCanvasCoords(e);
      const grab = 8 / zoom;
      const thin = 6 / zoom;
      let part = null;
      if (selectedMarkup !== null && markups[selectedMarkup]) part = hitPart(withBox(toContent(markups[selectedMarkup], pageBox)), coords, grab, thin);
      if (!part) { const i = hitIndex(coords); part = i !== null ? 'move' : null; }
      setHoverPart(part);
    }

    if (!isDrawing || readOnly) return;

    const coords = getCanvasCoords(e);

    if (tool === 'pen') {
      setCurrentPoints(prev => [...prev, coords]);
    } else {
      setCurrentPoints([coords]);
    }
  };

  // DP274-P3a-1 — one place where a new mark is born: it carries its page, the
  // author's colour, and it opens the text line so a mark can say something.
  const addMark = (shape, askForText) => {
    const mark = {
      ...toSheet(shape, pageBox),
      id: `markup-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
      color,
      weight: weightChoice,
      page: currentPage,
      status: 'open',
      round: authorKind === 'firm' ? 0 : 1,
      created_at: new Date().toISOString(),
    };   // no raw shape after this line: toSheet already carries its geometry
    dirtyRef.current.add(mark.id);
    setMarkups(prev => [...prev, mark]);
    if (askForText) {
      // The provisional number counts the marks the server has not numbered
      // yet, so a second unsaved Point reads 2 and not 1 again.
      const pending = (markups || []).filter((m) => !m.number && (m.type === 'point' || String(m.text || '').trim())).length;
      setTextPosition({ markId: mark.id, provisional: maxNumber(markups) + pending + 1, type: mark.type });
      setTextInput('');
      setShowTextInput(true);
    }
    return mark;
  };

  const handlePointerUp = (e) => {
    if (e && activePointer.current !== null && e.pointerId !== activePointer.current) return;
    activePointer.current = null;
    if (dragRef.current) {
      const d = dragRef.current;
      dragRef.current = null;
      if (d.moved) {
        const m = markups[d.index];
        if (m) dirtyRef.current.add(m.id);   // it moved: the next save posts it
      } else {
        setHistory((h) => h.slice(0, -1));   // a plain click: no history entry
      }
      return;
    }
    if (isPanning) {
      setIsPanning(false);
      setLastPanPoint(null);
      return;
    }

    if (!isDrawing || !startPoint) return;

    const endPoint = currentPoints[currentPoints.length - 1] || startPoint;
    const shape = { type: tool, x: startPoint.x, y: startPoint.y };
    if (tool === 'arrow') {
      // MK-3 — the press is the point; the box sits where the drag ended, or up and to the right if there was no drag.
      shape.head = 'start';
      const pulled = Math.hypot(endPoint.x - startPoint.x, endPoint.y - startPoint.y) >= 6 / zoom;
      shape.width = pulled ? endPoint.x - startPoint.x : 90 / zoom;
      shape.height = pulled ? endPoint.y - startPoint.y : -50 / zoom;
    } else if (tool === 'highlight' || tool === 'cloud' || tool === 'bracket') {
      shape.width = endPoint.x - startPoint.x;
      shape.height = endPoint.y - startPoint.y;
    } else if (tool === 'pen') {
      shape.points = currentPoints;
    }
    addMark(shape, true);
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPoints([]);
  };

  // DP274-P3a-1 — the line of text belongs to the mark just made. Escape keeps
  // the mark and writes nothing.
  const handleAddText = () => {
    const markId = textPosition?.markId;
    const words = textInput.trim();
    if (markId) {
      dirtyRef.current.add(markId);
      setMarkups(prev => prev.map(m => (m.id === markId ? { ...m, text: words } : m)));
    }
    setTextInput('');
    setShowTextInput(false);
    setTextPosition(null);
  };

  // History management
  const saveHistory = () => {
    setHistory(prev => [...prev.slice(-19), [...markups]]);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const previousState = history[history.length - 1];
    setMarkups(previousState);
    setHistory(prev => prev.slice(0, -1));
  };

  // MK-2b — a mark that was never saved just goes; a saved one asks its door
  // first, and stays if the door refuses (answered notes are resolved, not deleted).
  const handleDelete = async () => {
    if (selectedMarkup === null) return;
    const gone = markups[selectedMarkup];
    if (!gone) return;
    const saved = Boolean(gone.author) || Boolean(gone.number);
    if (saved) {
      try {
        if (onDeleteMark) await onDeleteMark(gone.id);
        else if (apiBase) await axios.delete(`${apiBase}/markups/${gone.id}`);
        else throw new Error('no door');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'That note could not be deleted');
        return;
      }
    }
    saveHistory();
    dirtyRef.current.delete(gone.id);
    setMarkups((prev) => prev.filter((m) => m.id !== gone.id));
    setSelectedMarkup(null);
    loadPending();
  };

  // MK-5a — a choice made while a mark is selected changes THAT mark; otherwise it is for the next one.
  const chooseInk = (hex) => {
    setInkChoice(hex);
    if (selectedMarkup === null) return;
    const m = markups[selectedMarkup];
    if (!m) return;
    saveHistory();
    dirtyRef.current.add(m.id);
    setMarkups((prev) => prev.map((x) => (x.id === m.id ? { ...x, color: hex } : x)));
  };
  const chooseWeight = (w) => {
    setWeightChoice(w);
    if (selectedMarkup === null) return;
    const m = markups[selectedMarkup];
    if (!m) return;
    saveHistory();
    dirtyRef.current.add(m.id);
    setMarkups((prev) => prev.map((x) => (x.id === m.id ? { ...x, weight: w } : x)));
  };
  const strip = selectedMarkup !== null && markups[selectedMarkup] ? markups[selectedMarkup].type : tool;
  const showInk = COLOURABLE.includes(strip);
  const showWeight = WEIGHTED.includes(strip);
  const current = selectedMarkup !== null && markups[selectedMarkup] ? markups[selectedMarkup] : null;

  // DP274-P3a-1 — post only what was touched, then take the server's merged
  // list as the truth (it holds the numbers it assigned).
  const handleSave = async (action) => {
    const dirty = markups.filter(m => dirtyRef.current.has(m.id));
    const post = action || onSaveMerged;
    if (post) {
      const merged = await post(dirty.length ? dirty : markups);
      if (Array.isArray(merged)) {
        setMarkups(merged);
        dirtyRef.current = new Set();
        setLegacyCount(0);
        toast.success('Marks saved');
        return;
      }
    }
    onSave(markups);
    dirtyRef.current = new Set();
    toast.success('Marks saved');
  };

  // The schedule: every numbered mark on this sheet, in number order.
  // MK-4 — every mark is on the list, by kind then number; a mark not yet saved sorts last within its kind.
  const KIND_ORDER = ['point', 'arrow', 'bracket', 'cloud', 'highlight', 'pen'];
  const scheduled = [...markups].sort((a, b) =>
    KIND_ORDER.indexOf(kindOf(a)) - KIND_ORDER.indexOf(kindOf(b)) || (a.number || 9999) - (b.number || 9999));
  const openCount = scheduled.filter(m => (m.status || 'open') === 'open').length;

  const markApi = (mark, suffix) => `${apiBase || ''}/markups/${mark.id}/${suffix}`;

  const canStatus = onStatus !== null && (onStatus || apiBase);
  const canReply = Boolean(onReply || apiBase);

  // DP274-P3a-4 — answers do not travel by themselves. This lists the parties
  // owed a word about their own notes; the firm sends it, from the button only.
  const [pending, setPending] = useState([]);
  const loadPending = useCallback(async () => {
    if (authorKind !== 'firm' || !apiBase) return;
    try {
      const r = await axios.get(`${apiBase}/answers-pending`);
      setPending(Array.isArray(r.data) ? r.data : []);
    } catch { setPending([]); }
  }, [authorKind, apiBase]);

  useEffect(() => { loadPending(); }, [loadPending]);

  const sendAnswers = async (row) => {
    try {
      const r = await axios.post(`${apiBase}/send-answers`, { membership_id: row.membership_id });
      toast.success(`Sent to ${r.data?.to || row.email}`);
      loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Those answers did not go'); }
  };

  const setStatus = async (mark, status) => {
    try {
      const r = onStatus ? await onStatus(mark.id, status)
        : (await axios.post(markApi(mark, 'status'), { status })).data;
      if (r) setMarkups(prev => prev.map(m => (m.id === mark.id ? r : m)));
      loadPending();
    } catch { toast.error('Could not change that note'); }
  };

  const sendReply = async (mark) => {
    const text = String(replyText[mark.id] || '').trim();
    if (!text) return;
    try {
      const r = onReply ? await onReply(mark.id, text)
        : (await axios.post(markApi(mark, 'replies'), { text })).data;
      if (r) setMarkups(prev => prev.map(m => (m.id === mark.id ? r : m)));
      setReplyText(prev => ({ ...prev, [mark.id]: '' }));
      loadPending();
    } catch { toast.error('Could not send that reply'); }
  };

  // Clicking a row brings its mark to the middle of the view.
  const revealMark = (mark) => {
    const index = markups.findIndex(m => m.id === mark.id);
    setSelectedMarkup(index >= 0 ? index : null);
    if (mark.page && mark.page !== currentPage) setCurrentPage(mark.page);
    const on = toContent(mark, pageBox);
    const box = containerRef.current?.getBoundingClientRect();
    if (box) setPan({ x: box.width / 2 - on.x * zoom, y: box.height / 2 - on.y * zoom });
  };

  // Zoom controls
  const handleZoomIn = () => { touchedRef.current = true; setZoom(prev => Math.min(prev * 1.2, 5)); };
  const handleZoomOut = () => { touchedRef.current = true; setZoom(prev => Math.max(prev / 1.2, 0.2)); };
  const handleResetZoom = () => fitToWindow();

  // MK-1 — the whole sheet, centred, with a margin; the page's own centring
  // (centredPage) does the rest once pan is zero.
  const fitToWindow = useCallback(() => {
    const box = containerRef.current;
    if (!box || !imageDimensions.width || !imageDimensions.height) return;
    setZoom(fitZoom(imageDimensions, box));
    setPan({ x: 0, y: 0 });
  }, [imageDimensions.width, imageDimensions.height]);

  // Once per sheet: the first time its size is known, fit it.
  useEffect(() => {
    if (fittedRef.current && touchedRef.current) return;
    if (!imageDimensions.width || !imageDimensions.height) return;
    if (imageDimensions.width === 800 && imageDimensions.height === 600 && !loadedImage && !isPdf) return; // the placeholder size
    fittedRef.current = true;
    fitToWindow();
  }, [imageDimensions.width, imageDimensions.height, loadedImage, isPdf, pageBox, fitToWindow]);

  // MK-1 — the wheel scrolls the sheet; with Ctrl/⌘ it zooms at the cursor.
  // Attached by hand because React's onWheel is passive and cannot
  // preventDefault, and the page behind must not scroll.
  useEffect(() => {
    const box = containerRef.current;
    if (!box) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      touchedRef.current = true;
      if (e.ctrlKey || e.metaKey) {
        const rect = box.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        setZoom((z) => {
          const next = Math.max(0.05, Math.min(z * (e.deltaY < 0 ? 1.1 : 1 / 1.1), 5));
          // keep the sheet point under the cursor where it is
          setPan((p) => ({ x: cx - ((cx - p.x) / z) * next, y: cy - ((cy - p.y) / z) * next }));
          return next;
        });
        return;
      }
      const dx = e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX;
      const dy = e.shiftKey && !e.deltaX ? 0 : e.deltaY;
      setPan((p) => ({ x: p.x - dx, y: p.y - dy }));
    };
    box.addEventListener('wheel', onWheel, { passive: false });
    return () => box.removeEventListener('wheel', onWheel);
  }, []);

  // MK-1 — Space held pans, whatever tool is up.
  useEffect(() => {
    const down = (e) => { if (e.code === 'Space' && e.target === document.body) { spaceRef.current = true; e.preventDefault(); } };
    const up = (e) => { if (e.code === 'Space') spaceRef.current = false; };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  // MK-2 — Delete removes the selected mark, Escape drops the selection (not while typing).
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      if (e.key === 'Escape') setSelectedMarkup(null);
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedMarkup !== null && !readOnly) { e.preventDefault(); handleDelete(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedMarkup, readOnly]);

  return (
    <div className={embedded ? 'kmk-embed flex flex-col' : 'fixed inset-0 bg-[#0D0D0D] z-50 flex flex-col'}>
      {/* Toolbar with Breadcrumb */}
      <div className="kmk-bar" data-testid="markup-bar">
        <button type="button" className="kmk-bar__close" onClick={onClose} title="Close" data-testid="markup-close">×</button>
        {!embedded ? (
          <span className="kmk-bar__crumb">
            {projectName ? <><span>{projectName}</span><i>›</i></> : null}
            <span>Drawing Control</span><i>›</i>
            {drawingNumber ? <b>{drawingNumber}</b> : null}
            <span className="kmk-bar__title">{drawingTitle}</span>
            {versionNumber > 0 ? <span>v{versionNumber}</span> : null}
          </span>
        ) : null}
        {!readOnly ? (
          <span className="kmk-tools" data-testid="markup-tools">
            {MARKUP_TOOLS.map((t) => (
              <button key={t.id} type="button" className="kmk-tool" aria-pressed={tool === t.id}
                onClick={() => setTool(t.id)} data-testid={`markup-tool-${t.id}`}>
                {ICONS[t.id]}<span>{t.label}</span>
              </button>
            ))}
          </span>
        ) : null}
        <span className="kmk-bar__sp">
          <span className="kmk-view">
            <button type="button" className="kmk-ic" onClick={handleZoomOut} title="Zoom out" data-testid="markup-zoom-out">−</button>
            <b>{Math.round(zoom * 100)}%</b>
            <button type="button" className="kmk-ic" onClick={handleZoomIn} title="Zoom in" data-testid="markup-zoom-in">+</button>
            <button type="button" className="kmk-ic kmk-ic--word" onClick={fitToWindow} title="Fit the sheet to the window" data-testid="markup-fit">Fit</button>
          </span>
          {!readOnly ? (
            <>
              <button type="button" className="kmk-ic kmk-ic--word" onClick={handleUndo} disabled={history.length === 0} title="Undo" data-testid="markup-undo">Undo</button>
              <button type="button" className="kmk-ic kmk-ic--word" onClick={handleDelete} disabled={selectedMarkup === null} title="Delete the selected mark" data-testid="markup-delete">Delete</button>
              {(saveButtons || []).length ? (saveButtons || []).map((b) => (
                <button key={b.id} type="button" className={b.brand ? 'kso-abtn brand' : 'kso-abtn'}
                  onClick={() => (b.asks ? setAsking(b) : handleSave(b.action))} data-testid={`markup-${b.id}`}>
                  {b.label}
                </button>
              )) : (
                <button type="button" className="kso-abtn brand" onClick={() => handleSave()} data-testid="markup-save">Save marks</button>
              )}
            </>
          ) : null}
        </span>
      </div>

      <div className="kmk-opts" data-testid="markup-options">
          {!readOnly && showInk ? (
            <span className="kmk-opts__grp">
              <span className="kmk-opts__k">{current ? `${codeFor(current, markups)} colour` : 'colour'}</span>
              {PALETTE(tokens).filter(([name, hex]) => hex && (name !== 'yellow' || strip === 'highlight')).map(([name, hex]) => (
                <button type="button" key={name} className="kmk-sw" style={{ background: hex }} title={name}
                  aria-pressed={(current ? current.color : color) === hex}
                  onClick={() => chooseInk(hex)} data-testid={`markup-ink-${name}`} />
              ))}
            </span>
          ) : null}
          {!readOnly && showWeight ? (
            <span className="kmk-opts__grp">
              <span className="kmk-opts__k">weight</span>
              {WEIGHTS.map((w) => (
                <button type="button" key={w} className="kmk-wt" title={`${w}`}
                  aria-pressed={(current ? (current.weight || 1.5) : weightChoice) === w}
                  onClick={() => chooseWeight(w)} data-testid={`markup-weight-${String(w).replace('.', '_')}`}>
                  <i style={{ height: `${w}px` }} />
                </button>
              ))}
            </span>
          ) : null}
          <span className="kmk-dim" data-testid="markup-dim">
            <span className="kmk-opts__k">sheet</span>
            <input type="range" min="25" max="100" step="5" value={dim}
              onChange={(e) => setDimKept(parseInt(e.target.value, 10))}
              onDoubleClick={() => setDimKept(100)}
              title="Dim the sheet so the marks come forward · double-click to reset"
              data-testid="markup-dim-slider" />
            <b>{dim}%</b>
          </span>
      </div>

      {/* Canvas Area with PDF/Image Background */}
      <div 
        ref={containerRef}
        className={embedded ? 'kmk-embed__sheet relative overflow-hidden' : 'flex-1 overflow-hidden relative'}
        style={{ cursor: isPanning ? 'grabbing' : (tool === 'select' ? cursorFor(hoverPart) : (MARKUP_TOOLS.find(t => t.id === tool)?.cursor || 'default')) }}
      >
        {/* PDF Background */}
        {isPdf && imageUrl && (
          <div 
            ref={pdfContainerRef}
            className="absolute inset-0 overflow-visible"
            style={{
              // The same transform the overlay canvas applies (translate, then
              // scale, from the top left), so a mark and the sheet cannot drift
              // apart at any zoom.
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              filter: dimFilter,
              transformOrigin: 'top left'
            }}
          >
            <Document
              file={imageUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="flex items-center gap-2 text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Loading PDF...
                </div>
              }
              error={
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <FileText className="w-12 h-12" />
                  <span>Failed to load PDF</span>
                </div>
              }
            >
              <div
                ref={pageElRef}
                style={{ position: 'absolute',
                         left: `${centredPage.left}px`,
                         top: `${centredPage.top}px` }}
              >
                <Page
                  pageNumber={currentPage}
                  onLoadSuccess={onPageLoadSuccess}
                  onRenderSuccess={measurePage}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-2xl"
                />
              </div>
            </Document>
          </div>
        )}

        {/* Markup Canvas Overlay */}
        <canvas
          ref={canvasRef}
          className={isPdf ? 'absolute inset-0 pointer-events-auto' : ''}
          style={{ background: isPdf ? 'transparent' : undefined, touchAction: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDoubleClick={(e) => {
            if (readOnly || !pageBox) return;
            const coords = getCanvasCoords(e);
            const i = hitIndex(coords);
            if (i === null) return;
            const m = markups[i];
            setSelectedMarkup(i);
            setTextPosition({ markId: m.id, type: m.type, provisional: m.number || '' });
            setTextInput(String(m.text || ''));
            setShowTextInput(true);
          }}
          onAuxClick={(e) => e.preventDefault()}
          onContextMenu={(e) => { if (e.button === 1) e.preventDefault(); }}
        />

        {/* Page Navigation for PDF */}
        {isPdf && numPages > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#1A1A1A] rounded-lg px-3 py-2 border border-[#2A2A2A]">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-gray-300 text-sm min-w-[60px] text-center">
              Page {currentPage} / {numPages}
            </span>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7"
              onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
              disabled={currentPage >= numPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Text Input Dialog */}
      {showTextInput && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
          <div className="bg-[#1A1A1A] p-4 rounded-lg border border-[#2A2A2A] w-80">
            <Label className="text-white" data-testid="markup-text-label">
              {textPosition?.markId ? `${codeFor(markups.find((m) => m.id === textPosition.markId) || {}, markups)} ·` : 'Text (optional)'}
            </Label>
            <textarea
              autoFocus
              rows={3}
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddText(); }
                if (e.key === 'Escape') { setShowTextInput(false); setTextInput(''); setTextPosition(null); }
              }}
              data-testid="markup-text-input"
              className="kso-in mt-2 w-full"
              placeholder="The note (Shift+Enter for a new line)"
            />
            <div className="flex gap-2 mt-3">
              <button type="button" className="kso-btn" onClick={() => { setShowTextInput(false); setTextInput(''); setTextPosition(null); }}>
                No text
              </button>
              <button type="button" className="kso-btn brand" onClick={handleAddText} data-testid="markup-text-done">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {asking ? (
        <div className="kmk-ask" data-testid="markup-ask">
          <label htmlFor="markup-ask-input">{asking.asks}</label>
          <input id="markup-ask-input" className="kso-in" value={askText} autoFocus
            onChange={(e) => setAskText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') { setAsking(null); setAskText(''); } }}
            data-testid="markup-ask-input" />
          <button type="button" className="kso-btn" data-testid="markup-ask-cancel"
            onClick={() => { setAsking(null); setAskText(''); }}>Not yet</button>
          <button type="button" className="kso-btn brand" data-testid="markup-ask-send"
            onClick={async () => {
              const words = askText;
              const action = asking.action;
              setAsking(null); setAskText('');
              await handleSave((marks) => action(marks, words));
            }}>{asking.confirm || 'Send'}</button>
        </div>
      ) : null}

      {/* DP274-P3a-1 — which sheet this is, and which page of it. */}
      <div className="kso-card__hd" data-testid="markup-sheet-head">
        <span>{[drawingNumber, `rev ${versionNumber}`, drawingTitle].filter(Boolean).join(' · ')}</span>
        <span className="n">page {currentPage} of {numPages || 1}</span>
      </div>

      {legacyCount > 0 ? (
        <div className="kmk-legacy kso-tag wait" data-testid="markup-legacy-note">
          {legacyCount} older mark{legacyCount === 1 ? '' : 's'} placed before positions were tied
          to the sheet — check them, then save
        </div>
      ) : null}

      {/* The notes schedule: every numbered mark on this sheet. */}
      <div className="kmk-sched" data-testid="markup-schedule">
        <div className="kso-card__hd">
          <span>Notes on this sheet</span>
          <span className="n">{scheduled.length} · {openCount} open</span>
          {pending.map((row) => (
            <button
              type="button"
              key={row.membership_id}
              className="kso-btn brand"
              onClick={() => sendAnswers(row)}
              data-testid={`markup-send-answers-${row.membership_id}`}
            >
              Send answers to {row.name} · {row.count}
            </button>
          ))}
        </div>
        <div className="kso-card__bd">
          {scheduled.length === 0 && (
            <p className="kmk-sched__none">No notes yet. Drop a Point where a question belongs.</p>
          )}
          {scheduled.map((mark) => {
            const status = mark.status || 'open';
            return (
              <div key={mark.id} className="kso-row" data-testid={`markup-note-${mark.number || 'new'}`}>
                <button
                  type="button"
                  className={status === 'resolved' ? 'kso-tag' : 'kso-tag you'}
                  onClick={() => revealMark(mark)}
                  data-testid={`markup-note-number-${mark.number || 'new'}`}
                >
                  {codeFor(mark, markups)}
                </button>
                {mark.rfi_number ? (
                  <span className="kso-tag" data-testid={`markup-rfi-${mark.number || 'new'}`}>
                    {mark.rfi_number}
                  </span>
                ) : null}
                <div className="kmk-sched__body">
                  <button type="button" className="kmk-sched__text" onClick={() => revealMark(mark)}>
                    {String(mark.text || '').trim() || '— no text —'}
                  </button>
                  <div className="kmk-sched__meta">
                    {[mark.author?.name || 'this firm',
                      (mark.round || 0) === 0 ? 'firm' : `round ${mark.round}`,
                      String(mark.created_at || '').slice(0, 10),
                      mark.type].filter(Boolean).join(' · ')}
                  </div>
                  {(mark.replies || []).map((r, ri) => (
                    <div key={ri} className="kmk-sched__reply">
                      {r.by?.name || 'someone'} · {String(r.at || '').slice(0, 10)}: {r.text}
                    </div>
                  ))}
                  {!readOnly && (canReply || canStatus) && (
                    <div className="kmk-sched__acts">
                      <Input
                        value={replyText[mark.id] || ''}
                        onChange={(e) => setReplyText(prev => ({ ...prev, [mark.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && sendReply(mark)}
                        placeholder="Reply"
                        data-testid={`markup-reply-input-${mark.number || 'new'}`}
                      />
                      <button type="button" className="kso-btn" onClick={() => sendReply(mark)}
                        data-testid={`markup-reply-send-${mark.number || 'new'}`}>Reply</button>
                      {canStatus ? (
                        <>
                          <button type="button" className="kso-btn" onClick={() => setStatus(mark, 'answered')}
                            data-testid={`markup-answered-${mark.number || 'new'}`}>Answered</button>
                          <button type="button" className="kso-btn" onClick={() => setStatus(mark, 'resolved')}
                            data-testid={`markup-resolved-${mark.number || 'new'}`}>Resolved</button>
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
                <span className={status === 'resolved' ? 'kso-tag ok' : 'kso-tag wait'}
                  data-testid={`markup-status-${mark.number || 'new'}`}>{status}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
